import { Component, ElementRef, ViewChild, ViewEncapsulation, type OnDestroy, type OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, type FormControlStatus, type UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject, Subscription, type Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { ScrollService } from '@fcom/common/services';
import type { Location } from '@fcom/core-api';
import type { LocationMatchData } from '@fcom/core-api/interfaces';
import type { Profile } from '@fcom/core-api/login';
import type { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { LocationMatchOnline, LocationRouteCffService } from '@fcom/core/services';
import { EMAIL_REGEX, LocalDate, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import {
  AUTOCOMPLETE_DEBOUNCE_TIME,
  FormStatus,
  dateValidator,
  disableConditionalFields,
  enableConditionalFields,
  frequentFlyerValidator,
  locationFormatValidator,
  pnrFormatValidator,
  trackFormState,
} from '@fcom/service-forms';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import { AutocompleteTheme } from '@fcom/service-forms/components/common/locations-autocomplete.component';
import {
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  ButtonType,
  MAX_ATTACHMENTS_5,
  NotificationLayout,
  NotificationTheme,
  RadioItemGroupOption,
  TEXTAREA_LIMIT_100,
  TEXTAREA_LIMIT_1000,
  TEXTAREA_LIMIT_3000,
  TextInputType,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';
import type { AttachmentResponse } from '@fcom/ui-components/components/forms/interfaces';
import { AttachmentStatus, type Attachment } from '@fcom/ui-components/components/interfaces';
import { LanguageService } from '@fcom/ui-translate';

import {
  FileOrigin,
  FormType,
  SpecialAssistanceFormFields,
  type CasePostMessage,
  type SpecialAssistanceSubmissionData,
} from '../interfaces';
import { ClaimOrFeedbackService } from '../services/claim-or-feedback.service';
import { FLIGHT_NUMBER_PATTERN, PHONE_NUMBER_PATTERN, parsePhoneNumber } from '../utils';

const REQUEST_SPECIAL_ASSISTANCE = 'request-special-assistance';

@Component({
  selector: 'fin-special-assistance-form',
  templateUrl: 'special-assistance-form.component.html',
  styleUrls: ['./special-assistance-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SpecialAssistanceFormComponent implements OnInit, OnDestroy {
  readonly ButtonType = ButtonType;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly SpecialAssistanceFormFields = SpecialAssistanceFormFields;
  readonly TextInputType = TextInputType;

  readonly maxAttachments: FileUploaderComponent['maxAttachments'] = MAX_ATTACHMENTS_5;
  readonly maxSizeInBytes: FileUploaderComponent['maxSizeInBytes'] = ATTACHMENT_MAX_SIZE_IN_BYTES;
  readonly textareaLimit1000 = TEXTAREA_LIMIT_1000;
  readonly textareaLimit3000 = TEXTAREA_LIMIT_3000;
  readonly textareaLimit100 = TEXTAREA_LIMIT_100;

  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;
  @ViewChild('formContainer', { static: true }) formContainer: ElementRef;

  destinationLoading$: Subject<boolean> = new Subject();
  departureLoading$: Subject<boolean> = new Subject();
  destinationLocations$: Subject<Location[]> = new Subject();
  departureLocations$: Subject<Location[]> = new Subject();
  minDate: LocalDate;
  maxDate: LocalDate;
  reactiveForm: UntypedFormGroup;
  formStatus: FormStatus = FormStatus.INITIAL;
  completedServiceRequestNumber: string;
  duplicateServiceRequestNumber: string;

  specialAttentionReasonOptions: RadioItemGroupOption[] = [
    { value: 'wheelChairOrMobA' },
    { value: 'hearingImpaired' },
    { value: 'visuallyImpaired' },
    { value: 'visuallyImpairedWithGuideDog' },
    { value: 'travelingWithServiceDog' },
    { value: 'pregnant' },
    { value: 'medicalCondition' },
    { value: 'medicalEquipment' },
    { value: 'other' },
  ];

  wheelchairTypeOptions: RadioItemGroupOption[] = [
    { value: 'wheelchairTypeElectricDry' },
    { value: 'wheelchairTypeElectricWet' },
    { value: 'wheelchairTypeLithiumIon' },
    { value: 'wheelchairTypeManual' },
    { value: 'otherMobA' },
  ];

  medicalConditionOptions: RadioItemGroupOption[] = [
    { value: 'myocardialInfarction' },
    { value: 'cerebralCiculatoryDisorder' },
    { value: 'recentInjury' },
    { value: 'recentSurgery' },
    { value: 'symptomaticCoronaryDisease' },
    { value: 'chronicObstructivePulmanoryDisease' },
    { value: 'psychiatricDisorder' },
    { value: 'infectiousDisease' },
    { value: 'anemia' },
    { value: 'chickenpox' },
    { value: 'otherCondition' },
  ];

  otherReasonForAssistanceOptions: RadioItemGroupOption[] = [
    { value: 'alzheimer' },
    { value: 'downSyndrome' },
    { value: 'autism' },
    { value: 'dementia' },
    { value: 'intellectualDisabilities' },
  ];

  specialAttentionFields: SpecialAssistanceFormFields[] = [
    SpecialAssistanceFormFields.OWN_WHEELCHAIR,
    SpecialAssistanceFormFields.NEED_ASSISTANCE_WHERE,
    SpecialAssistanceFormFields.TRAVELING_WITH_ASSISTANT,
    SpecialAssistanceFormFields.ASSISTANT_NAME,
    SpecialAssistanceFormFields.MEDICAL_CONDITION,
    SpecialAssistanceFormFields.MEDICAL_EQUIPMENT,
    SpecialAssistanceFormFields.ADD_MEDICAL_EQUIPMENT,
  ];

  wheelchairFields: SpecialAssistanceFormFields[] = [
    SpecialAssistanceFormFields.WHEELCHAIR_TYPE,
    SpecialAssistanceFormFields.WHEELCHAIR_DEPTH,
    SpecialAssistanceFormFields.WHEELCHAIR_HEIGHT,
    SpecialAssistanceFormFields.WHEELCHAIR_WEIGHT,
    SpecialAssistanceFormFields.WHEELCHAIR_WIDTH,
    SpecialAssistanceFormFields.ABLE_TO_SIT_WITH_KNEES_BENT,
  ];

  noWheelchairFields: SpecialAssistanceFormFields[] = [
    SpecialAssistanceFormFields.REASON_FOR_WHEELCHAIR,
    SpecialAssistanceFormFields.ABLE_TO_SIT_WITH_KNEES_BENT,
  ];

  otherMobilityFields: SpecialAssistanceFormFields[] = [SpecialAssistanceFormFields.OTHER_MOBILITY_AID_DESCRIPTION];

  medicalFields: SpecialAssistanceFormFields[] = [
    SpecialAssistanceFormFields.CAPAP_DEVICE_MODEL,
    SpecialAssistanceFormFields.PORTABLE_OXYGEN_CONCENTRATOR_MODEL,
    SpecialAssistanceFormFields.OTHER_MEDICAL_EQUIPMENT_TYPE,
    SpecialAssistanceFormFields.OTHER_MEDICAL_EQUIPMENT_MODEL,
  ];

  otherReasonFields: SpecialAssistanceFormFields[] = [SpecialAssistanceFormFields.OTHER_REASON];

  nonRequired: SpecialAssistanceFormFields[] = [
    SpecialAssistanceFormFields.ASSISTANT_NAME,
    SpecialAssistanceFormFields.ADD_MEDICAL_EQUIPMENT,
    SpecialAssistanceFormFields.CAPAP_DEVICE_MODEL,
    SpecialAssistanceFormFields.PORTABLE_OXYGEN_CONCENTRATOR_MODEL,
    SpecialAssistanceFormFields.OTHER_MEDICAL_EQUIPMENT_TYPE,
    SpecialAssistanceFormFields.OTHER_MEDICAL_EQUIPMENT_MODEL,
  ];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private claimAndFeedbackService: ClaimOrFeedbackService,
    private scrollService: ScrollService,
    private gtmService: GtmService,
    private store$: Store<AppState>
  ) {}

  get attachments(): Attachment[] {
    return (
      this.fileUploader?.attachments?.filter((a: Attachment): boolean => a?.status === AttachmentStatus.READY) || []
    );
  }

  get formDisabled(): boolean {
    return this.formStatus === FormStatus.LOADING || Boolean(this.fileUploader?.isUploading);
  }

  get isLoading(): boolean {
    return this.formStatus === FormStatus.LOADING;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), take(1)).subscribe((profile: Profile): void => {
        this.createForm(profile);
        this.addFormWatchers();
      })
    );

    this.destinationLoading$.next(false);
    this.departureLoading$.next(false);

    this.minDate = LocalDate.now();
    this.maxDate = LocalDate.forDate(new Date().getFullYear() + 1, 12, 31);
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(profile?: Profile): void {
    this.reactiveForm = this.fb.group({
      firstName: [
        profile?.firstname || '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)],
      ],
      lastName: [
        profile?.lastname || '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)],
      ],
      phoneNumber: [parsePhoneNumber(profile), [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]], // International format, plus followed by max length string
      email: [profile?.email ?? '', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      cardNumber: [profile?.memberNumber ?? '', [frequentFlyerValidator()]],
      flightDate: ['', [Validators.required, dateValidator()]],
      flightNumber: ['', [Validators.required, Validators.pattern(FLIGHT_NUMBER_PATTERN)]], // any 2 alphabets, any 1-4 numbers
      origin: ['', [Validators.required, locationFormatValidator()]],
      destination: ['', [Validators.required, locationFormatValidator()]],
      reservationNumber: ['', [Validators.required, pnrFormatValidator()]],
      reasonForSpecialAttention: ['', Validators.required],
      ownWheelchair: [{ value: '', disabled: true }, [Validators.required]],
      wheelchairType: [{ value: '', disabled: true }, [Validators.required]],
      wheelchairDepth: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^\d+$/)]], // Only numbers
      wheelchairHeight: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^\d+$/)]], // Only numbers
      wheelchairWeight: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^\d+$/)]], // Only numbers
      wheelchairWidth: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^\d+$/)]], // Only numbers
      addMedicalEquipment: [{ value: '', disabled: true }, [Validators.maxLength(this.textareaLimit1000)]], // 1000 is limit set in legacy form
      reasonForWheelchair: [
        { value: '', disabled: true },
        [Validators.required, Validators.maxLength(this.textareaLimit3000)],
      ],
      ableToSitWithKneesBent: [{ value: '', disabled: true }, [Validators.required]],
      needAssistanceWhere: [{ value: '', disabled: true }, [Validators.required]],
      travelingWithAssistant: [{ value: '', disabled: true }, [Validators.required]],
      assistantName: [{ value: '', disabled: true }, [Validators.maxLength(100)]], // magic number 100 just to limit needlessly long answers
      medicalCondition: [{ value: '', disabled: true }, [Validators.required]],
      medicalEquipment: [{ value: '', disabled: true }, [Validators.required]],
      CAPAPDeviceModel: [{ value: '', disabled: true }, [Validators.required]],
      portableOxygenConcentratorModel: [{ value: '', disabled: true }, [Validators.required]],
      otherMedicalEquipmentType: [{ value: '', disabled: true }, [Validators.required]],
      otherMedicalEquipmentModel: [{ value: '', disabled: true }, [Validators.required]],
      otherReasonForAssistance: [{ value: '', disabled: true }, [Validators.required]],
      additionalInformation: ['', [Validators.maxLength(this.textareaLimit1000)]], // 1000 is limit set in legacy form
      [SpecialAssistanceFormFields.MEDA_ATTACHMENT]: [[]],
      lithiumBatteries: [{ value: '', disabled: true }, [Validators.required]],
      lithiumBattery1Power: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(20)]],
      lithiumBattery2Power: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(20)]],
      otherMobilityAidDescription: [
        { value: '', disabled: true },
        [Validators.required, Validators.maxLength(this.textareaLimit100)],
      ],
    });
  }

  removeFile(): void {
    this.reactiveForm.get(SpecialAssistanceFormFields.MEDA_ATTACHMENT).reset();
  }

  updateConditionalFields(inputValue: string): void {
    let fieldsToEnable: SpecialAssistanceFormFields[] = [];
    const fieldsToDisable: SpecialAssistanceFormFields[] = this.wheelchairFields
      .concat(this.noWheelchairFields)
      .concat(this.specialAttentionFields)
      .concat(this.medicalFields)
      .concat(this.otherMobilityFields)
      .concat(this.otherReasonFields);
    switch (inputValue) {
      case 'wheelChairOrMobA':
        fieldsToEnable = [
          SpecialAssistanceFormFields.OWN_WHEELCHAIR,
          SpecialAssistanceFormFields.NEED_ASSISTANCE_WHERE,
          SpecialAssistanceFormFields.TRAVELING_WITH_ASSISTANT,
          SpecialAssistanceFormFields.ASSISTANT_NAME,
          SpecialAssistanceFormFields.ADD_MEDICAL_EQUIPMENT,
          SpecialAssistanceFormFields.ABLE_TO_SIT_WITH_KNEES_BENT,
        ];
        break;
      case 'medicalCondition':
        fieldsToEnable = [SpecialAssistanceFormFields.MEDICAL_CONDITION];
        break;
      case 'medicalEquipment':
        fieldsToEnable = [SpecialAssistanceFormFields.MEDICAL_EQUIPMENT];
        break;
      case 'other':
        fieldsToEnable = [SpecialAssistanceFormFields.OTHER_REASON];
        break;
      default:
        break;
    }

    disableConditionalFields(fieldsToDisable, this.reactiveForm);
    enableConditionalFields(fieldsToEnable, this.reactiveForm);
  }

  addFormWatchers(): void {
    this.watchFieldChanges(
      this.reactiveForm,
      this.updateConditionalFields.bind(this),
      SpecialAssistanceFormFields.REASON_FOR_SPECIAL_ATTENTION
    );
    this.watchFieldChanges(
      this.reactiveForm,
      this.updateWheelChairFields.bind(this),
      SpecialAssistanceFormFields.OWN_WHEELCHAIR
    );
    this.watchFieldChanges(
      this.reactiveForm,
      this.updateMedicalEquipmentFields.bind(this),
      SpecialAssistanceFormFields.MEDICAL_EQUIPMENT
    );
    this.watchFieldChanges(
      this.reactiveForm,
      this.updateWheelChairType.bind(this),
      SpecialAssistanceFormFields.WHEELCHAIR_TYPE
    );
    this.watchFieldChanges(
      this.reactiveForm,
      this.updateLithiumBatteriesPowerFields.bind(this),
      SpecialAssistanceFormFields.LITHIUM_BATTERIES
    );

    this.subscriptions.add(
      this.reactiveForm.statusChanges
        .pipe(
          filter((status: FormControlStatus): boolean => status === 'INVALID'),
          distinctUntilChanged(),
          take(1)
        )
        .subscribe((): void => {
          trackFormState(REQUEST_SPECIAL_ASSISTANCE, 'start', this.gtmService);
        })
    );

    this.onDestinationChanges();
    this.onDepartureChanges();
  }

  updateWheelChairFields(inputValue: string): void {
    switch (inputValue) {
      case 'ownWheelchairYes':
        disableConditionalFields(this.noWheelchairFields, this.reactiveForm);
        enableConditionalFields(this.wheelchairFields, this.reactiveForm);
        break;
      case 'ownWheelchairNo':
        disableConditionalFields(this.wheelchairFields, this.reactiveForm);
        enableConditionalFields(this.noWheelchairFields, this.reactiveForm);
        break;
      default:
        break;
    }
  }

  updateWheelChairType(inputValue: string): void {
    if (inputValue === 'otherMobA') {
      enableConditionalFields(this.otherMobilityFields, this.reactiveForm);
    } else {
      disableConditionalFields(this.otherMobilityFields, this.reactiveForm);
    }
    if ('wheelchairTypeLithiumIon' === inputValue) {
      enableConditionalFields([SpecialAssistanceFormFields.LITHIUM_BATTERIES], this.reactiveForm);
    } else {
      disableConditionalFields([SpecialAssistanceFormFields.LITHIUM_BATTERIES], this.reactiveForm);
    }
  }

  updateLithiumBatteriesPowerFields(inputValue: string): void {
    if ('lithiumBattery1' === inputValue) {
      enableConditionalFields([SpecialAssistanceFormFields.LITHIUM_BATTERY_1_POWER], this.reactiveForm);
    } else if ('lithiumBattery2' === inputValue) {
      enableConditionalFields(
        [SpecialAssistanceFormFields.LITHIUM_BATTERY_1_POWER, SpecialAssistanceFormFields.LITHIUM_BATTERY_2_POWER],
        this.reactiveForm
      );
    } else {
      disableConditionalFields(
        [SpecialAssistanceFormFields.LITHIUM_BATTERY_1_POWER, SpecialAssistanceFormFields.LITHIUM_BATTERY_2_POWER],
        this.reactiveForm
      );
    }
  }

  uploadFile(): (file: File) => Observable<AttachmentResponse> {
    return (file: File): Observable<AttachmentResponse> => {
      return this.claimAndFeedbackService.uploadFile<AttachmentResponse>(file, FileOrigin.CASE);
    };
  }

  updateMedicalEquipmentFields(inputValue: string): void {
    const fieldsToDisable: SpecialAssistanceFormFields[] = this.medicalFields;
    let fieldsToEnable: SpecialAssistanceFormFields[] = [];

    switch (inputValue) {
      case 'CAPAPDevice':
        fieldsToEnable = [SpecialAssistanceFormFields.CAPAP_DEVICE_MODEL];
        break;
      case 'portableOxygenConcentrator':
        fieldsToEnable = [SpecialAssistanceFormFields.PORTABLE_OXYGEN_CONCENTRATOR_MODEL];
        break;
      case 'otherMedicalEquipment':
        fieldsToEnable = [
          SpecialAssistanceFormFields.OTHER_MEDICAL_EQUIPMENT_TYPE,
          SpecialAssistanceFormFields.OTHER_MEDICAL_EQUIPMENT_MODEL,
        ];
        break;
      default:
        break;
    }

    disableConditionalFields(fieldsToDisable, this.reactiveForm);
    enableConditionalFields(fieldsToEnable, this.reactiveForm);
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(REQUEST_SPECIAL_ASSISTANCE, 'send-attempt', this.gtmService);
    this.submitToSalesforce();
  }

  private submitToSalesforce(): void {
    const oldData: SpecialAssistanceSubmissionData = this.reactiveForm.value;
    this.subscriptions.add(
      this.claimAndFeedbackService
        .createCase<{ caseId: string }>({
          formType: FormType.MEDA,
          customerFields: {
            memberNumber: oldData?.cardNumber?.toString(),
          },
          category: {
            formCategory: '',
            formSubCategory: oldData?.reasonForSpecialAttention,
          },
          bookingInformation: {
            flightDate: oldData?.flightDate,
            flightNumber: oldData?.flightNumber,
            destination: oldData?.destination as Location,
            origin: oldData?.origin as Location,
            reservationNumber: oldData?.reservationNumber,
          },
          addPassengers: {
            mainPassenger: {
              firstName: oldData.firstName,
              lastName: oldData.lastName,
              email: oldData.email,
              phone: oldData.phoneNumber,
              country: this.languageService.langValue.toLocaleLowerCase() === 'fi-fi' ? 'FI' : 'GB',
              attachment: undefined,
            },
            companions: [],
          },
          optionalFeedback: {
            additionalComments: oldData?.additionalInformation,
          },
          specialAssistance: {
            ableToSitWithKneesBent: oldData?.ableToSitWithKneesBent === 'ableToSitWithKneesBentYes',
            additionalInformation: oldData?.additionalInformation,
            additionalMedicalEquipment: oldData?.addMedicalEquipment,
            assistantName: oldData?.assistantName,
            assistanceType: oldData?.needAssistanceWhere,
            capapDeviceModel: oldData?.CAPAPDeviceModel,
            reasonForWheelchair: oldData?.reasonForWheelchair,
            travellingWithAssistant: oldData?.travelingWithAssistant === 'travelingWithAssistantYes',
            typeOfMedicalEquipment: oldData?.medicalEquipment,
            typeOfCondition: oldData?.medicalCondition,
            otherReasonForAssistance: oldData?.otherReasonForAssistance,
            ownWheelchair: oldData?.ownWheelchair === 'ownWheelchairYes',
            otherMedicalEquipmentModel: oldData?.otherMedicalEquipmentModel,
            otherMedicalEquipmentType: oldData?.otherMedicalEquipmentType,
            portableOxygenConcentratorModel: oldData?.portableOxygenConcentratorModel,
            wheelchairDepth: oldData?.wheelchairDepth ? parseFloat(oldData?.wheelchairDepth) : undefined,
            wheelchairHeight: oldData?.wheelchairHeight ? parseFloat(oldData?.wheelchairHeight) : undefined,
            wheelchairType: oldData?.wheelchairType,
            wheelchairWidth: oldData?.wheelchairWidth ? parseFloat(oldData?.wheelchairWidth) : undefined,
            wheelchairWeight: oldData?.wheelchairWeight ? parseFloat(oldData?.wheelchairWeight) : undefined,
            [SpecialAssistanceFormFields.MEDA_ATTACHMENT]: this.attachments,
            amountOfLithiumIonBatteries: this.amountOfLithiumIonBatteriesSwitch(oldData?.lithiumBatteries),
            battery1Power: oldData?.lithiumBattery1Power,
            battery2Power: oldData?.lithiumBattery2Power,
            otherMobilityAidDescription: oldData?.otherMobilityAidDescription,
          },
        })
        .pipe(take(1))
        .subscribe({
          next: (result: CasePostMessage): void => {
            if (result.caseId) {
              this.completedServiceRequestNumber = result.caseId;
              trackFormState(REQUEST_SPECIAL_ASSISTANCE, 'success', this.gtmService);
            }
            this.formStatus = FormStatus.COMPLETE;
          },
          error: (): void => {
            this.formStatus = FormStatus.ERROR;
            trackFormState(REQUEST_SPECIAL_ASSISTANCE, 'error', this.gtmService);
          },
        })
    );
  }

  private queryLocations(queryString: string, locations$: Subject<Location[]>, loading$: Subject<boolean>): void {
    const locale: string = this.languageService.localeValue;

    loading$.next(true);

    this.subscriptions.add(
      this.locationRouteCffService
        .locationMatchesFor(queryString, locale, LocationMatchOnline.ALL)
        .subscribe((matchedLocations: LocationMatchData): void => {
          loading$.next(false);
          locations$.next(matchedLocations.items);
        })
    );
  }

  private onDepartureChanges(): void {
    this.subscriptions.add(
      this.reactiveForm
        .get('origin')
        .valueChanges.pipe(debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME))
        .subscribe((val) => {
          if (this.reactiveForm.get('origin').status === 'INVALID') {
            this.queryLocations(val, this.departureLocations$, this.departureLoading$);
          }
        })
    );
  }

  private onDestinationChanges(): void {
    this.subscriptions.add(
      this.reactiveForm
        .get('destination')
        .valueChanges.pipe(debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME))
        .subscribe((val) => {
          if (this.reactiveForm.get('destination').status === 'INVALID') {
            this.queryLocations(val, this.destinationLocations$, this.destinationLoading$);
          }
        })
    );
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  private watchFieldChanges(form: UntypedFormGroup, callback: Function, field: string): void {
    this.subscriptions.add(
      form.get(field).valueChanges.subscribe((val) => {
        callback(val);
      })
    );
  }

  get datepickerThemes(): typeof DatepickerTheme {
    return DatepickerTheme;
  }

  get autocompleteThemes(): typeof AutocompleteTheme {
    return AutocompleteTheme;
  }

  amountOfLithiumIonBatteriesSwitch(data: string): 1 | 2 | null {
    switch (data) {
      case 'lithiumBattery1':
        return 1;
      case 'lithiumBattery2':
        return 2;
      default:
        return null;
    }
  }
}
